// src/components/shared/CheckInScale.jsx
//
// Shared pieces for the reflective "lighter check-in" used by TeenHubView and
// SpouseHubView. Two exports:
//
//   <FaceScale />      — a tap-a-face 1..5 input for one prompt (replaces the
//                        old native range sliders, which felt stuck on touch).
//   <CheckInResults /> — the calm results screen shown after submit: this
//                        check-in visualized, a gentle since-last-time trend,
//                        the reflection, a streak line, and next-step nudges.
//   <StreakHeader />   — a small dashboard-style card for the weekly pulse:
//                        🔥 streak line + a "done / due this week" pill.
//   checkedInThisWeek / computeStreak / hasBaseline — pure cadence helpers
//                        derived from the persisted checkins array (no new
//                        persistence, no scoring).
//
// Tone rules (do not break these): the check-in is REFLECTIVE, never scored.
// No numbers, no percentages, no labels, no alarm-red. Trends respect each
// prompt's valence via `higherIsBetter` (e.g. stress/worry going up is shown
// in soft amber, never as a "worse score").
//
// Prompt shape expected by both components:
//   { id, label, low, high, higherIsBetter, faces: [{ emoji, word } x5] }

import React from 'react';
import { HeartPulse, RefreshCw, Sparkles } from 'lucide-react';

// Tailwind needs literal class names, so accents are enumerated, not composed.
const ACCENTS = {
    indigo: {
        text: 'text-indigo-600',
        selectedBg: 'bg-indigo-50',
        ring: 'ring-indigo-300',
        dot: 'bg-indigo-500',
        panel: 'bg-indigo-50/60 border-indigo-100',
    },
    rose: {
        text: 'text-rose-600',
        selectedBg: 'bg-rose-50',
        ring: 'ring-rose-300',
        dot: 'bg-rose-500',
        panel: 'bg-rose-50/60 border-rose-100',
    },
};
const accentOf = (accent) => ACCENTS[accent] || ACCENTS.indigo;

// Calm-only trend tones. Deliberately no red anywhere.
const TREND_TONES = {
    emerald: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50',
    slate: 'text-slate-500 bg-slate-100',
};

// --- WEEKLY CADENCE HELPERS (pure, derived from the checkins array) ---
// These power the gentle "weekly pulse" framing. They read the existing
// persisted history (`[{ date: ISOstring, answers }]`) and derive everything —
// no new persistence, no scoring. A "week" is a rolling 7-day window counted
// back from now, which keeps the math kind: there's no Sunday-midnight cliff.

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Valid, non-future timestamps from the history array (guards bad/absent data).
const checkinTimes = (checkins) => {
    const now = Date.now();
    return (Array.isArray(checkins) ? checkins : [])
        .map((c) => new Date(c?.date).getTime())
        .filter((t) => !isNaN(t) && t <= now);
};

// Has the user checked in within the last 7 days?
export const checkedInThisWeek = (checkins) =>
    checkinTimes(checkins).some((t) => Date.now() - t < WEEK_MS);

// Consecutive recent 7-day windows (counting back from now) with ≥1 check-in.
// If this week is done, it counts; if this week is still open but the prior
// weeks form an unbroken run, that prior run is returned (an "at-risk" streak
// worth keeping alive). A fully skipped week breaks the chain.
export const computeStreak = (checkins) => {
    const now = Date.now();
    // Window index: 0 = the last 7 days, 1 = the 7 days before that, etc.
    const windows = new Set(checkinTimes(checkins).map((t) => Math.floor((now - t) / WEEK_MS)));
    if (windows.size === 0) return 0;
    let w = windows.has(0) ? 0 : 1; // allow a still-open current week
    if (!windows.has(w)) return 0;
    let streak = 0;
    while (windows.has(w)) {
        streak += 1;
        w += 1;
    }
    return streak;
};

// Has the user ever checked in? (Used for gentle first-time framing.)
export const hasBaseline = (checkins) => (Array.isArray(checkins) ? checkins : []).length > 0;

// --- STREAK HEADER (calm dashboard-style card, no scores) ---
// status: 'done' (checked in within the last 7 days) | 'due' (this week's is open).
export const StreakHeader = ({ streak = 0, status = 'due', accent = 'indigo' }) => {
    const a = accentOf(accent);
    const done = status === 'done';
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-2xl leading-none" aria-hidden="true">🔥</span>
                <div className="min-w-0">
                    <p className="font-black text-slate-800 leading-tight">
                        {streak > 0 ? `${streak}-week streak` : 'Start your streak'}
                    </p>
                    <p className="text-xs text-slate-400 leading-snug">One check-in a week keeps it going.</p>
                </div>
            </div>
            {done ? (
                <span className={`inline-flex items-center text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${TREND_TONES.emerald}`}>
                    Checked in this week ✓
                </span>
            ) : (
                <span className={`inline-flex items-center text-xs font-bold px-3 py-1.5 rounded-full shrink-0 ${a.selectedBg} ${a.text}`}>
                    This week’s check-in
                </span>
            )}
        </div>
    );
};

// --- TAP-A-FACE INPUT (one prompt) ---
export const FaceScale = ({ prompt, value, onChange, accent = 'indigo' }) => {
    const a = accentOf(accent);
    return (
        <div>
            <label className="block font-bold text-slate-800 mb-3">{prompt.label}</label>
            <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
                {prompt.faces.map((face, i) => {
                    const v = i + 1;
                    const selected = value === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onChange(v)}
                            aria-label={face.word}
                            aria-pressed={selected}
                            className={`min-h-[64px] px-1 py-2.5 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 select-none transition-all duration-200 ${
                                selected
                                    ? `${a.selectedBg} border-transparent ring-2 ${a.ring} scale-110 shadow-md`
                                    : 'bg-white border-slate-200 opacity-60 grayscale hover:opacity-100 hover:grayscale-0 hover:border-slate-300 active:scale-95'
                            }`}
                        >
                            <span className="text-2xl sm:text-3xl leading-none" aria-hidden="true">{face.emoji}</span>
                            <span className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wide leading-tight ${selected ? a.text : 'text-slate-400'}`}>
                                {face.word}
                            </span>
                        </button>
                    );
                })}
            </div>
            <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-2">
                <span>{prompt.low}</span>
                <span>{prompt.high}</span>
            </div>
        </div>
    );
};

// Gentle, valence-aware trend for one prompt. Wording steers toward the
// prompt's own end-words ("closer to Quiet") instead of better/worse language.
const trendFor = (prompt, current, previous) => {
    if (current == null || previous == null) return null;
    if (current === previous) {
        return { arrow: '—', text: 'about the same as last time', tone: 'slate' };
    }
    const up = current > previous;
    const gentler = prompt.higherIsBetter ? up : !up; // is this the easier direction?
    return {
        arrow: up ? '↑' : '↓',
        text: `a little closer to “${up ? prompt.high : prompt.low}” since last time`,
        tone: gentler ? 'emerald' : 'amber',
    };
};

// "1st", "2nd", "3rd", "4th"... for the streak line.
const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// 1–2 supportive next-step nudges, pointing only at sections that exist on the
// page the check-in lives on. Which surface we're on is inferred from the
// prompt ids (teen: sleep/stress/mood/connect · spouse: energy/supported/time/worry).
const suggestionsFor = (answers) => {
    const out = [];
    if (answers.stress != null || answers.connect != null) {
        // Teen surface
        if ((answers.stress ?? 3) >= 4) out.push('The “Calm” card up top has a couple of things to try when the noise picks up.');
        if ((answers.sleep ?? 3) <= 2) out.push('The “Sleep” card up top has a few small, tonight-sized things worth a look.');
        if ((answers.connect ?? 3) <= 2) out.push('“You are not alone”, further down the page, is a good place to start — one message counts.');
        if ((answers.mood ?? 3) <= 2) out.push('The “Stress & identity” cards below were written for weeks that feel like this one.');
        if (out.length === 0) out.push('Things look pretty steady — the pillar cards up top are there whenever you feel like a small experiment.');
    } else if (answers.energy != null || answers.worry != null) {
        // Spouse / partner surface
        if ((answers.energy ?? 3) <= 2) out.push('“You Can’t Pour From an Empty Cup”, in the Caregiver Wellbeing cards above, was written for weeks like this.');
        if ((answers.worry ?? 3) >= 4) out.push('“Watch for the Quiet Burnout”, up in the Caregiver Wellbeing cards, is worth two quiet minutes.');
        if ((answers.supported ?? 3) <= 2) out.push('Feeling under-held is worth saying out loud — telling one person the honest version of how you are tends to shift it.');
        if ((answers.time ?? 3) <= 2) out.push('“Your Own Oxygen Mask”, in the cards above, is a gentle nudge to book one hour that’s just yours.');
        if (out.length === 0) out.push('A steadier stretch — the Family Snapshot below is a nice thing to bring into the huddle.');
    }
    return out.slice(0, 2);
};

// --- RESULTS SCREEN (rendered inside the existing check-in card) ---
export const CheckInResults = ({ prompts, answers, history = [], reflection, onStartOver, accent = 'indigo' }) => {
    const a = accentOf(accent);
    const count = Math.max(history.length, 1);
    // The last history entry is this session; the one before it is the comparison.
    const previous = history.length >= 2 ? history[history.length - 2]?.answers : null;
    const suggestions = suggestionsFor(answers);

    return (
        <div className="animate-in fade-in">
            {/* 1. Supportive header */}
            <div className={`flex items-center gap-2 mb-5 text-sm font-black uppercase tracking-widest ${a.text}`}>
                <HeartPulse size={16} /> Just for you
            </div>

            {/* 2. This check-in, visualized */}
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 px-4 sm:px-5 py-1 mb-6">
                {prompts.map((p) => {
                    const v = answers[p.id];
                    const face = v != null ? p.faces[v - 1] : null;
                    return (
                        <div key={p.id} className="flex items-center gap-3 sm:gap-4 py-3.5 border-b border-slate-100 last:border-0">
                            <span className="text-3xl w-10 text-center shrink-0" aria-hidden="true">{face?.emoji}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 leading-tight">{p.label}</p>
                                <p className="font-black text-slate-800">{face?.word}</p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0" aria-hidden="true">
                                {[1, 2, 3, 4, 5].map((d) => (
                                    <span key={d} className={`w-2.5 h-2.5 rounded-full transition-colors ${v >= d ? a.dot : 'bg-slate-200'}`} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 3. Trend since last time — only once there's a previous check-in */}
            {previous && (
                <div className="mb-6">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Since last time</p>
                    <div className="space-y-2">
                        {prompts.map((p) => {
                            const t = trendFor(p, answers[p.id], previous[p.id]);
                            if (!t) return null;
                            return (
                                <div key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="text-sm text-slate-600 flex-1 min-w-[10rem]">{p.label}</span>
                                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${TREND_TONES[t.tone]}`}>
                                        <span aria-hidden="true">{t.arrow}</span> {t.text}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 4. The reflection */}
            <div className={`rounded-2xl border p-5 mb-5 ${a.panel}`}>
                <p className="text-slate-800 text-lg leading-relaxed">{reflection}</p>
            </div>

            {/* 5. Streak line */}
            <p className="text-sm text-slate-500 mb-5">
                Your <span className="font-bold text-slate-700">{ordinal(count)}</span> check-in — showing up counts.
            </p>

            {/* 6. Gentle next steps */}
            {suggestions.length > 0 && (
                <div className="mb-7">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">If you want a next step</p>
                    <div className="space-y-2">
                        {suggestions.map((s, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm text-slate-700">
                                <Sparkles size={15} className={`${a.text} shrink-0 mt-0.5`} />
                                <span className="leading-relaxed">{s}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 7. Start over */}
            <button
                onClick={onStartOver}
                className="inline-flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-colors"
            >
                <RefreshCw size={16} /> Check in again
            </button>
        </div>
    );
};
